import { Property, Unit } from "types/landlord/address";
import { Partner } from "types/landlord/partner";
import { Template } from "types/landlord/verificationTemplate";
import {
  ApplicantType,
  ApplicationStatus,
  DecisionAction,
  HousingAddress,
  Invitee,
  VerificationNames,
  VerificationStatus,
  ApplicationFeeStatus,
  ExecutionModes,
  ApplicantSettings,
  RequiresOverridesIdentityVerification,
  ApplicantStatus,
  ScreeningStatus,
  RetryIdentityVerificationStatus,
  AppliedDiscountInfo,
  PreferredSubmissionType,
  CriteriaRentType,
  FeeStatusItem
} from "shared/types";
import { Money, VerificationReport } from "types/landlord/reports";
import {
  Rule,
  RuleSetDecision
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { CriteriaTypeOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/constants";
import { CoSignerSettings } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import { VerificationMetrics } from "views/applications-id/components/IncomeSummary/types";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import { RecommendationSummary } from "api/recommendation-summary/types";

import { ApplicationNote } from "shared/app/Notes/types";

type GroupStat = {
  average_credit_score?: number;
  min_credit_score?: number;
  max_credit_score?: number;
  no_credit_score_people?: number;
  self_reported_gross_income?: number;
  self_reported_gross_income_to_rent?: number;
  gross_income?: number;
  gross_income_to_rent_ratio?: number;
  net_income?: number;
  debt_to_net_income?: number;
};

interface ApplicantCriteria {
  applicant_id: number;
  criteria_id: null | number;
  created_at: string;
  id: number;
  score: null | number;
  updated_at: string;
  settings: {
    bankruptcy_records: number;
    credit_score: number;
    gross_income: number;
    gross_income_to_rent_ratio: number;
    housing_court_records: number;
    net_income: number;
    past_due_account: number;
    sex_offender_check: number;
    unpaid_collections_balance: number;
    income_document_fraud_check: number;
  };
}

export interface Applicant {
  verification_template_id?: number;
  disclosures?: string;
  role: null | ApplicantType;
  id: number;
  lead_id?: number;
  crm_contact_id?: number;
  refunded?: boolean;
  application_fee?: Money;
  paid_application_fee?: Money;
  refunded_application_fee?: Money;
  template_fees_total?: Money;
  application_status: ApplicantStatus;
  template_criteria_settings?: CoSignerSettings | ApplicantSettings;
  fee?: Money;
  can_checkout?: boolean;
  first_name: string;
  last_name: string;
  middle_name?: string;
  email: string;
  phone: string;
  verifications: Verification[];
  application_started_at?: string;
  application_finished_at?: string;
  application_submitted_at?: string;
  first_submitted_at?: string;
  application_id: number;
  criteria?: ApplicantCriteria[];
  pending_county_reports?: boolean;
  criteria_check_settings?: CriteriaCheckSettings;
  pending_magic_links: {
    email: string;
    phone: string | null;
    role: string;
  }[];
  application_fee_status: ApplicationFeeStatus | null;
  fee_statuses?: FeeStatusItem[];
  pet_rent_amount: Money;
  housing_assistance_voucher_amount: Money;

  housing_addresses?: HousingAddress[];
  canceled_by_auto_cancel?: boolean;
  application_canceled_at?: string;
  is_manual: boolean;
  has_plaid_data: boolean;

  finished_verifications: {
    total_steps: number;
    finished_steps: number;
  };
  retry_identity_verification_status?: RetryIdentityVerificationStatus;
  discount_info?: AppliedDiscountInfo;
  verifications_metrics?: VerificationMetrics;
  preferred_submission_disclosure_type?: PreferredSubmissionType;

  total_additional_documents: number;
  document_requests?: DocumentRequest[];
  audit_summary_flags?: {
    application_id_changed: boolean;
    role_changed: boolean;
    unit_id_changed: boolean;
  };
  is_primary?: boolean;

  credit_report_verifications?: VerificationReport[];
  canadian_credit_report_verifications?: VerificationReport[];
  combined_income_verifications?: VerificationReport[];
  identity_verification_verifications?: VerificationReport[];
  itemized_receipt_consented_at?: string;
  application_support_agent_active: boolean;
}

export interface DocumentRequest {
  id: number;
  note: string;
  is_uploaded: boolean;
  seen_by_partner_user: boolean;
  created_at: string;
  requested_by_name: string | null;
}

type CriteriaCheckSetting = {
  value: number | string | null;
  check: boolean;
  importance?: number | null;
};

type CreditCheckSetting = CriteriaCheckSetting & {
  type: CriteriaTypeOptions;
};

export type CriteriaCheckSettings = Partial<{
  bankruptcy_records: CriteriaCheckSetting;
  credit_score: CreditCheckSetting;
  gross_income: CriteriaCheckSetting;
  gross_income_to_rent_ratio: CriteriaCheckSetting;
  housing_court_records: CriteriaCheckSetting;
  net_income: CriteriaCheckSetting;
  past_due_account: CriteriaCheckSetting;
  sex_offender_check: CriteriaCheckSetting;
  unpaid_collections_balance: CriteriaCheckSetting;
  collections_balance: CriteriaCheckSetting;
  income_document_fraud_check: CriteriaCheckSetting;
}>;

interface Verification {
  id: number;
  name: VerificationNames;
  status: VerificationStatus;
  editable: boolean;
  index: number;
  estimated_time: number;
  title: string;
  description: string;
  progress: {
    total_steps: number;
    filled_steps: number;
  };
  created_at: string;
  updated_at: string;
}

export interface Reasons {
  rule_set_id: string;
  rule_set_type: RuleSetDecision;
  rules: Rule[];
}

export enum MakeDecisionRequirements {
  ApplicationNotReady = "application_not_ready",
  HasPendingCountyReports = "has_pending_county_reports",
  NoRecommendation = "no_recommendation"
}

export interface Application {
  id: number;
  lead_id?: number;
  refunded?: boolean;
  created_at?: string;
  email?: string;
  property: Property;
  partner: Partner;
  unit: Unit;
  desired_move_in_date?: string;
  desired_lease_term?: number;
  lease_term_enabled: boolean;
  status: ApplicationStatus;
  total_applicants?: number;
  monthly_rent_amount: Money;
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
  pets_count: number;
  pet_rent_value: Money;
  has_voucher?: boolean;
  voucher_amount?: Money;
  applicants: Applicant[];
  updated_at?: string;
  started_at?: string;
  submitted_at?: string;
  first_submitted_at?: string;

  make_decision_requirements?: MakeDecisionRequirements[];
  // BoomFinance-side application id, used to deep-link into the lender portal
  // via the partner magic link. Present once the application has a BoomFinance
  // (purchase) application.
  boom_finance_external_id?: string | null;
  verification_template?: Template;
  score?: number;
  requires_manual_or_conditional_execution?: {
    applicants?: {
      id: number;
      name: string;
      role: ApplicantType;
      verifications: {
        execution_mode: ExecutionModes;
        id: number;
        name: VerificationNames;
      }[];
    }[];
    total_verifications?: number;
  };
  rule_reasons?: Reasons[];
  recommendation?: DecisionAction;
  criteria_check_settings?: CriteriaCheckSettings;
  pending_magic_links?: Invitee[];
  include_co_signer_in_criteria_calculation?: boolean;
  eligible_for_role_update: boolean;
  has_pending_verifications: boolean;
  requires_overrides_identity_verification?: RequiresOverridesIdentityVerification;
  requires_override_warnings_report?: RequiresOverridesIdentityVerification;
  decision_at?: string;
  group_stat: GroupStat;
  group_stat_v2_present?: boolean;
  auto_approved: boolean;
  screening_status: ScreeningStatus;
  partner_name_in_submission_disclosure?: string;
  bypass_requested?: {
    requested_at: string;
    applicant: Pick<
      Applicant,
      "id" | "first_name" | "last_name" | "middle_name"
    >;
  } | null;
  latest_application_rent_history?: AdjustedApplicationRentActivity;
  housing_assistance_voucher_amount?: Money;
  pet_rent_amount?: Money;
  has_processing_verification?: boolean;
  conditions?: string[];
  unit_delisted?: boolean;
  assignee_ids?: number[];
  assignees?: {
    id: number;
    name: string;
    email: string;
  }[];
  last_note?: ApplicationNote;
  // AI explanation of the current recommendation (BSCPF-6408). Merged outside
  // the detail cache; null when there is nothing to show for the current
  // recommendation.
  recommendation_summary?: RecommendationSummary | null;
}

export interface MergeApplicationsError {
  validation_errors: {
    target_application: Application | null;
    application: Application | null;
    errors: string[];
  }[];
}

interface BillableItem {
  amount?: {
    bank: {
      rounding_method: null | string;
      store: {
        rates: Record<string, unknown>;
        options: Record<string, unknown>;
        guard: Record<string, unknown>;
      };
    };
    currency: {
      alternate_symbols: string[];
      decimal_mark: string;
      disambiguate_symbol: string;
      format: null | string;
      html_entity: string;
      id: string;
      iso_code: string;
      iso_numeric: string;
      name: string;
      priority: number;
      smallest_denomination: number;
      subunit: string;
      subunit_to_unit: number;
      symbol: string;
      symbol_first: boolean;
      thousands_separator: string;
    };
    fractional: string;
  };
  fee: {
    cents: number;
    currency: string;
  };
  name: string;
}

export interface BillingActivityMetadata {
  name: string;
  unit: string;
  email: string;
  receipt_line_items: BillableItem[];
  items: Array<{
    fee: {
      cents: number;
      currency: string;
    };
    name: string;
    amount?: {
      bank: {
        store: {
          guard: Record<string, unknown>;
          rates: Record<string, unknown>;
          options: Record<string, unknown>;
        };
        rounding_method: string | null;
      };
      currency: {
        id: string;
        name: string;
        format: string | null;
        symbol: string;
        subunit: string;
        iso_code: string;
        priority: number;
        html_entity: string;
        iso_numeric: string;
        decimal_mark: string;
        symbol_first: boolean;
        subunit_to_unit: number;
        alternate_symbols: string[];
        disambiguate_symbol: string;
        thousands_separator: string;
        smallest_denomination: number;
      };
      fractional: string;
    };
  }>;
  product: string;
  property_id: number;
  property_name: string;
  one_line_address: string;
}

export interface BillingActivity {
  id: number;
  owner_type: string;
  owner_id: number;
  subject_type: string;
  subject_id: number;
  description: string;
  metadata: BillingActivityMetadata;
  kind: string;
  category: string;
  statement_id: string | null;
  amount_cents: number;
  amount_currency: string;
  created_at: string;
  updated_at: string;
  source: string;
  amount: Money;
  invoice_id: string | null;
  invoice_status: string | null;
}

export interface MergeApplicationsError {
  validation_errors: {
    target_application: Application | null;
    application: Application | null;
    errors: string[];
  }[];
}

interface BillableItem {
  amount?: {
    bank: {
      rounding_method: null | string;
      store: {
        rates: Record<string, unknown>;
        options: Record<string, unknown>;
        guard: Record<string, unknown>;
      };
    };
    currency: {
      alternate_symbols: string[];
      decimal_mark: string;
      disambiguate_symbol: string;
      format: null | string;
      html_entity: string;
      id: string;
      iso_code: string;
      iso_numeric: string;
      name: string;
      priority: number;
      smallest_denomination: number;
      subunit: string;
      subunit_to_unit: number;
      symbol: string;
      symbol_first: boolean;
      thousands_separator: string;
    };
    fractional: string;
  };
  fee: {
    cents: number;
    currency: string;
  };
  name: string;
}

export interface BillingActivityMetadata {
  name: string;
  unit: string;
  email: string;
  receipt_line_items: BillableItem[];
  items: Array<{
    fee: Money;
    name: string;
    amount?: {
      bank: {
        store: {
          guard: Record<string, unknown>;
          rates: Record<string, unknown>;
          options: Record<string, unknown>;
        };
        rounding_method: string | null;
      };
      currency: {
        id: string;
        name: string;
        format: string | null;
        symbol: string;
        subunit: string;
        iso_code: string;
        priority: number;
        html_entity: string;
        iso_numeric: string;
        decimal_mark: string;
        symbol_first: boolean;
        subunit_to_unit: number;
        alternate_symbols: string[];
        disambiguate_symbol: string;
        thousands_separator: string;
        smallest_denomination: number;
      };
      fractional: string;
    };
  }>;
  product: string;
  property_id: number;
  property_name: string;
  one_line_address: string;
}

export interface BillingActivity {
  id: number;
  owner_type: string;
  owner_id: number;
  subject_type: string;
  subject_id: number;
  description: string;
  metadata: BillingActivityMetadata;
  kind: string;
  category: string;
  statement_id: string | null;
  amount_cents: number;
  amount_currency: string;
  created_at: string;
  updated_at: string;
  source: string;
  amount: {
    cents: number;
    currency: string;
  };
  invoice_id: string | null;
  invoice_status: string | null;
}

export interface PreviewApplicationRentByUnit {
  monthly_rent_amount: Money;
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
  housing_assistance_voucher_amount?: Money;
  pet_rent_amount?: Money;
  criteria_rent_type?: CriteriaRentType;
}
